import { Button, ButtonVariants, ButtonSizes, TableCellTypes, FilterTypes } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { makeUnitTypeCell, makeUnitScopedCell, makeDiscrepanciesCell } from '@/components/common';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { buildRowUnitOptions, unitTypeLabel } from './declarationUnits';
import {
  DECLARATION_FILTERS,
  DECLARATION_SORT_FIELDS,
  DECLARATION_STATUS,
  isConsumedDeclaration,
} from './declarations.constants';
import { materialFilter, supplierFilter } from '@/config/filterDefinitions';
import {
  buildDeclarationStatusVariants,
  buildVerificationStatusVariants,
  buildDeclarationStatusFilterOptions,
  DECLARATION_FILTER_STATUSES,
} from '@/config/statuses';
import { DECLARATION_STATUS_TONE, VERIFICATION_STATUS_TONE } from '@/config/statusTones';
import { makeLinkCell } from '@/components/common/tableCells';

// `sortable` is derived, never hand-asserted — see CLAUDE.md.
const canSort = (id) => Boolean(DECLARATION_SORT_FIELDS[id]);

// Badge tone per backend enum code (labels come from the *_LABEL_KEYS maps).

export const declarationStatusTone = (code) => DECLARATION_STATUS_TONE[code];

export const buildFilters = (t, { materialOptions, supplierOptions } = {}) => [
  {
    field: DECLARATION_FILTERS.status,
    label: t('FIELD_STATUS'),
    type: FilterTypes.checkbox,
    options: buildDeclarationStatusFilterOptions(t, DECLARATION_FILTER_STATUSES),
  },
  ...materialFilter(t, DECLARATION_FILTERS.materialId, materialOptions),
  ...supplierFilter(t, DECLARATION_FILTERS.sentBySupplierId, supplierOptions),
];

export const buildRowActions = (t, { onValidate, onRetry, onFulfill }) => [
  {
    id: 'linkShipments',
    label: t('ACTION_LINK_SHIPMENTS'),
    variant: ButtonVariants.tertiary,
    // TODO: re-enable once the declaration ⇄ shipment link exists server-side.
    disabled: true,
    hidden: (row) => !['VERIFIED', 'MANUALLY_VERIFIED'].includes(row.verificationStatusValue),
  },
  {
    id: 'validate',
    label: t('DECLARATIONS_ACTION_VALIDATE'),
    variant: ButtonVariants.tertiary,
    onClick: onValidate,
    // Extraction has not finished, so there is nothing to validate yet — Retry takes the slot.
    hidden: (row) =>
      row.verificationStatusValue !== 'NA' || row.statusValue === DECLARATION_STATUS.processing,
  },
  {
    id: 'retry',
    label: t('DECLARATIONS_ACTION_RETRY'),
    variant: ButtonVariants.tertiary,
    onClick: onRetry,
    // TODO: re-enable once iscc-backend has the retry-extraction route.
    disabled: true,
    hidden: (row) => row.statusValue !== DECLARATION_STATUS.processing,
  },
  {
    id: 'fulfill',
    label: t('ACTION_FULFILL'),
    variant: ButtonVariants.tertiary,
    onClick: onFulfill,
    hidden: (row) => row.verificationStatusValue !== 'MANUALLY_VERIFIED',
  },
];

export const buildRowMenuActions = (
  t,
  { onAddAttachment, onEdit, onDelete, onSurplusWriteOff },
) => [
  {
    id: 'addAttachment',
    label: t('ACTION_ADD_ATTACHMENT'),
    onClick: onAddAttachment,
  },
  // TODO: no Tooltip in @carboledger/ui yet — surface the disabled reason once one exists.
  { id: 'edit', label: t('COMMON_EDIT'), onClick: onEdit, disabled: (row) => row.isInUse },
  {
    id: 'delete',
    label: t('COMMON_DELETE'),
    onClick: onDelete,
    disabled: (row) => row.isInUse || isConsumedDeclaration(row),
  },
  {
    id: 'addSurplusWriteOff',
    label: t('DECLARATIONS_ACTION_ADD_SURPLUS_WRITE_OFF'),
    onClick: onSurplusWriteOff,
    hidden: (row) => row.hasSurplusOrWriteOff,
    disabled: isConsumedDeclaration,
  },
  {
    id: 'editSurplusWriteOff',
    label: t('DECLARATIONS_ACTION_EDIT_SURPLUS_WRITE_OFF'),
    onClick: onSurplusWriteOff,
    hidden: (row) => !row.hasSurplusOrWriteOff,
    disabled: isConsumedDeclaration,
  },
];

// The same menu against one declaration, for the detail header — RowActionsMenu's item shape.
export const resolveRowMenuItems = (actions, row) =>
  actions
    .filter((action) => !action.hidden?.(row))
    .map((action) => ({
      id: action.id,
      label: action.label,
      disabled: Boolean(action.disabled?.(row)),
      onSelect: () => action.onClick?.(row),
    }));

// Hides the header's own Add button so this card's CTA is the only one shown.
export const buildEmptyState = (t, { onAdd }) => ({
  image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
  title: t('DECLARATIONS_LIST_EMPTY_TITLE'),
  description: t('DECLARATIONS_LIST_EMPTY_DESCRIPTION'),
  primaryLabel: t('ACTION_ADD_DECLARATIONS'),
  onPrimaryClick: onAdd,
  primaryIcon: <Plus />,
});

// Columns trimmed to what GET /:module/inbound actually returns.
const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderUnit = makeUnitScopedCell('unit');
const renderReceivedQty = makeUnitScopedCell('receivedQty');
const renderAdjustedQty = makeUnitScopedCell('adjustedQty');
const renderAvailableQty = makeUnitScopedCell('availableQty');
const renderWriteOffQty = makeUnitScopedCell('writeOffQty');

export const buildDeclarationsColumns = (
  t,
  { onOpenDiscrepancy, onView, onViewFile, unitHeaderSelect },
) =>
  [
    {
      id: 'batchId',
      header: t('DECLARATIONS_COL_REFERENCE_ID'),
      type: TableCellTypes.custom,
      width: 140,
      cell: makeLinkCell(onView),
    },
    {
      id: 'declarationNo',
      header: t('FIELD_DECLARATION_NO'),
      type: TableCellTypes.text,
      width: 140,
    },
    {
      id: 'materialName',
      header: t('FIELD_MATERIAL_NAME'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'supplierName',
      header: t('FIELD_SENT_BY'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: 'shippingFrom',
      header: t('DECLARATIONS_COL_SHIPPING_FROM'),
      type: TableCellTypes.text,
      width: 160,
    },
    {
      id: 'status',
      header: t('FIELD_STATUS'),
      type: TableCellTypes.statusDot,
      variantMap: buildDeclarationStatusVariants(t, DECLARATION_STATUS_TONE),
      width: 160,
    },
    {
      id: 'facilityName',
      header: t('FIELD_FACILITY_NAME'),
      type: TableCellTypes.text,
      width: 143,
    },
    {
      id: 'terminal',
      header: t('FIELD_TERMINAL'),
      type: TableCellTypes.text,
      width: 141,
    },
    {
      id: 'vessel',
      header: t('FIELD_VESSEL'),
      type: TableCellTypes.text,
      width: 141,
    },
    {
      id: 'unit',
      header: t('FIELD_UNIT'),
      type: TableCellTypes.custom,
      width: 100,
      cell: renderUnit,
    },
    {
      id: 'unitType',
      header: t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderUnitTypeCell,
      headerSelect: unitHeaderSelect,
    },
    {
      id: 'receivedQty',
      header: t('FIELD_RECEIVED_DECLARATION_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderReceivedQty,
    },
    {
      id: 'surplusQty',
      header: t('DECLARATIONS_COL_SURPLUS_QTY'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'writeOffQty',
      header: t('DECLARATIONS_COL_WRITE_OFF_QTY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderWriteOffQty,
    },
    {
      id: 'adjustedQty',
      header: t('DECLARATIONS_COL_ADJUSTED_QTY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderAdjustedQty,
    },
    {
      id: 'availableQty',
      header: t('FIELD_AVAILABLE_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderAvailableQty,
    },
    {
      id: 'ghgEmissions',
      header: t('FIELD_GHG_EMISSIONS'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'ghgSavings',
      header: t('FIELD_GHG_EMISSION_SAVINGS'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'rawMaterialType',
      header: t('FIELD_RAW_MATERIAL_TYPE'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'countryOfOrigin',
      header: t('FIELD_COUNTRY_OF_ORIGIN'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'feedstockCategory',
      header: t('FIELD_FEEDSTOCK_CATEGORY'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'allocationPreference',
      header: t('FIELD_ALLOCATION_PREFERENCE'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'dateOfDischarge',
      header: t('FIELD_DATE_OF_DISCHARGE'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: 'dateOfDispatch',
      header: t('FIELD_DATE_OF_DISPATCH'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: 'dateOfIssuance',
      header: t('DECLARATIONS_COL_DATE_OF_ISSUANCE'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: 'declarations',
      header: t('DECLARATIONS_COL_DECLARATIONS'),
      type: TableCellTypes.custom,
      width: 110,
      // The declaration itself — its PDF, not the row it belongs to.
      cell: ({ row }) => (
        <Button
          variant={ButtonVariants.tertiary}
          size={ButtonSizes.sm}
          disabled={!row.declarationFileUrl}
          onClick={() => onViewFile(row)}
        >
          {t('COMMON_VIEW')}
        </Button>
      ),
    },
    {
      id: 'verificationStatus',
      header: t('DECLARATIONS_COL_VERIFICATION'),
      type: TableCellTypes.statusDot,
      variantMap: buildVerificationStatusVariants(t, VERIFICATION_STATUS_TONE),
      width: 150,
    },
    {
      id: 'discrepancyCount',
      header: t('FIELD_DISCREPANCIES'),
      type: TableCellTypes.custom,
      width: 200,
      cell: makeDiscrepanciesCell(onOpenDiscrepancy),
    },
  ].map((column) => ({ ...column, pinnable: true, sortable: canSort(column.id) }));
